import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, HelpCircle } from "lucide-react";
import {
  insuranceOptions,
  mediaOptions,
  /* travelOptions, */ rootOptions,
} from "../../data/choiceData";
import { useNavigate } from "react-router-dom";

function OptionCard({ option, onClick }) {
  return (
    <motion.button
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="relative text-left bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm transition-all duration-300 cursor-pointer group focus:outline-none hover:shadow-xl hover:ring-2 hover:ring-emerald-400 focus:ring-2 focus:ring-emerald-400"
    >
      <div className="flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 h-48 sm:h-56 md:h-64">
        <div className="flex flex-1 justify-center items-center min-h-0 w-full mb-2 overflow-hidden">
          <img
            src={option.image}
            alt={option.title}
            className={`w-full h-full object-contain ${option.key === "road-trip" ? "scale-150" : ""}`}
          />
        </div>

        {/* Content */}
        <div className="text-center">
          <h4 className="mb-1 text-sm font-semibold text-gray-900 sm:mb-1.5 sm:text-base md:mb-2 md:text-lg">
            {option.title}
          </h4>
          <p className="text-xs leading-tight text-gray-600 sm:text-xs md:text-sm md:leading-relaxed">
            {option.subtitle}
          </p>
        </div>

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-gradient-to-b transition-all duration-300 pointer-events-none from-emerald-50/0 via-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50/20 group-hover:via-emerald-50/10 group-hover:to-emerald-50/20" />
      </div>
    </motion.button>
  );
}

export default function Choice() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("root");
  const [helperOpen, setHelperOpen] = useState(false);
  const [toast, setToast] = useState("");

  const options = useMemo(() => {
    if (stage === "insurance") return insuranceOptions;
    if (stage === "tourism") return mediaOptions;
    // if (stage === "travel") return travelOptions;
    return rootOptions;
  }, [stage]);

  const title = useMemo(() => {
    if (stage === "insurance") return "Choose Your Insurance";
    if (stage === "tourism") return "Media and Events";
    // if (stage === "travel") return "Choose Your Travel Cover";
    return "Choose Your Option";
  }, [stage]);

  const handleCardClick = (opt) => {
    if (stage === "root") {
      if (opt.key === "insurance") {
        // || opt.key === "travel") {
        setStage(opt.key);
      } else if (opt.key === "tourism" && opt.link) {
        navigate(opt.link);
      } else {
        setToast("This feature is coming soon");
        setTimeout(() => setToast(""), 1800);
      }
      return;
    }

    if (opt.comingSoon) {
      setToast("This item is coming soon");
      setTimeout(() => setToast(""), 1800);
      return;
    }

    // Here you could navigate or set a selection
    if (opt.link) {
      navigate(opt.link);
    }
  };

  return (
    <section className="px-4 py-6 -mt-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex gap-4 justify-between items-center mb-6">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              {title}
            </h2>
            <div className="mt-2 w-32 h-1 bg-emerald-500 rounded" />
          </div>

          <AnimatePresence initial={false}>
            {stage !== "root" && (
              <motion.button
                onClick={() => setStage("root")}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="inline-flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50"
              >
                <ChevronLeft size={16} /> Back to Main Menu
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"
          >
            {options.map((opt) => (
              <OptionCard
                key={opt.key}
                option={opt}
                onClick={() => handleCardClick(opt)}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Helper bubble */}
        <motion.button
          onClick={() => setHelperOpen((v) => !v)}
          className="inline-flex fixed bottom-4 left-4 z-30 justify-center items-center w-12 h-12 text-white bg-gray-900 rounded-full shadow-lg sm:left-6 sm:bottom-6"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <HelpCircle size={20} />
        </motion.button>

        <AnimatePresence>
          {helperOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.25 }}
              className="fixed left-4 sm:left-6 bottom-16 sm:bottom-20 z-30 p-3 sm:p-4 w-80 sm:w-72 max-w-[calc(100vw-2rem)] sm:max-w-none text-xs sm:text-sm text-gray-700 bg-white rounded-xl border border-gray-200 shadow-xl"
            >
              <p className="mb-2 font-semibold text-gray-900">How it works</p>
              <ul className="pl-4 space-y-1 list-disc sm:pl-5">
                <li>Choose an option to see available covers.</li>
                <li>Items marked as Coming soon are not yet available.</li>
                <li>Use "Back to options" to return to the main choices.</li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="fixed bottom-6 left-1/2 z-30 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg -translate-x-1/2"
            >
              {toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
